<template>
  <div class="results-body">
    <div class="results-intro u-light-blue u-bg-primary-light-gradient">
      <div class="container--large">
        <div class="results-intro__title-wrapper">
          <h1 class="results-intro__title u-primary-very-dark u-page-title" v-html="title"></h1>
          <span class="results-intro__timestamp" v-if="last_updated">Last updated {{ last_updated }}</span>
        </div>
        <div class="results-intro__summary u-primary-very-dark u-sans-serif u-type-20" style="white-space: pre-line;" v-html="summary"></div>

        <div v-if="featured_nodes" :class="featuredNodesClass + ' container--large'">
          <h2 :class="featuredNodesClass + '__title u-bg-secondary-dark'">
            {{ featuredNodesTitle }}
          </h2>
          <div class="featured-events__items u-border-color-primary-dark" v-html="featured_nodes"></div>
        </div>

        <filters></filters>
      </div>
    </div>
    <results-meta  @datesSetChanged="datesSetChanged" @calendarViewChanged="query"></results-meta>
    <template v-if="!calendarView">
      <results-list></results-list>
      <results-pagination @update="query" v-if="!calendarView"></results-pagination>
    </template>
  </div>
</template>

<script>

const typeFilters = window.drupalSettings.searchFilters.type
const resourceTypeFilters = window.drupalSettings.searchFilters.resource_type
const stateFilters = window.drupalSettings.searchFilters.state
const eventTypeFilters = window.drupalSettings.searchFilters.event_type
const regionFilters = window.drupalSettings.searchFilters.region
const showPastEventsFilters = window.drupalSettings.searchFilters.show_past_events
const issueFilters = window.drupalSettings.searchFilters.issues
const campaignFilters = window.drupalSettings.searchFilters.campaign
const roleFilters = window.drupalSettings.searchFilters.role
const professionalPracticeFilters = window.drupalSettings.searchFilters.professional_practice

const businessItemTypeFilters = window.drupalSettings.searchFilters.business_item_type
const outcomeFilters = window.drupalSettings.searchFilters.outcome
const strategicObjectiveFilters = window.drupalSettings.searchFilters.strategic_objective

const featuredNodes = window.drupalSettings.featuredNodes
const searchSettings = window.drupalSettings.searchSettings

// Use the RA engine key for flagship site only
const engineKey = (
    searchSettings.IS_MAIN_SITE &&
    "/" + drupalSettings.path.currentPath !== searchSettings.standard_events.path
)
  ? window.drupalSettings.ra.engineKey
  : window.drupalSettings.engineKey

const {
  EVENT_TID: eventTid,
  EYE_ON_EQUITY_TID: eyeOnEquityTid
} = window.drupalSettings.search.tids

import moment from "moment";

import calendar from '@/mixins/calendar'
import Filters from "./Filters";
import ResultsMeta from "./ResultsMeta";
import ResultsList from "./ResultsList";
import ResultsPagination from "./ResultsPagination";
import {mapGetters} from 'vuex';
import {expandInit} from "../../../storybook/stories/js/modules/expand.js";

export default {
  name: 'SearchAPI',
  mixins: [calendar],
  components: {
    Filters,
    ResultsMeta,
    ResultsList,
    ResultsPagination
  },
  watch: {
    autocompleteTerm: function () {
      if(this.hasKeyword && this.autocompleteTerm !== '' && this.autocompleteTerm.length >= 3) {
        this.autocompleteQuery()
      }
    },
    searchTerm: function () {
      this.$store.commit('updateCurrentPage', 1)
      this.query()
    },
    activeFilters: function () {
      if(!this.locked && !this.initial) {
        this.query()
      }
    },
    featured_nodes: function(value) {
      if (value) {
        this.$nextTick(() => {
          const expandEls = document.querySelectorAll(".js-expand.is-async");
          expandEls.forEach(el => expandInit(el));
        })
      }
    },
  },
  computed: {
    ...mapGetters({
      locked: 'getLocked',
      today: 'getToday',
      datesSet: 'getDatesSet',
      calendarView: 'getCalendarView',
      searchTerm: 'getSearchTerm',
      autocompleteTerm: 'getAutocompleteTerm',
      // searchQuery: 'getQuery',
      currentPage: 'getCurrentPage',
      perPage: 'getPerPage',
      perPageCalendar: 'getPerPageCalendar',
      resultsData: 'getResultsData',
      activeFilters: 'getActiveFilters',
      allTypeFilters: 'getAllTypeFilters',
      allStateFilters: 'getAllStateFilters',
      allIssueFilters: 'getAllIssueFilters',
      allRoleFilters: 'getAllRoleFilters',
      allProfessionalPracticeFilters: 'getAllProfessionalPracticeFilters',
      allCampaignFilters: 'getAllCampaignFilters',
      allResourceTypeFilters: 'getAllResourceTypeFilters',
      allEventTypeFilters: 'getAllEventTypeFilters',
      allRegionFilters: 'getAllRegionFilters',
      allShowPastEventsFilters: 'getAllShowPastEventsFilters',
      allBusinessItemTypeFilters: 'getAllBusinessItemTypeFilters',
      allOutcomeFilters: 'getAllOutcomeFilters',
      allStrategicObjectiveFilters: 'getAllStrategicObjectiveFilters',

      additionalParameters: 'getAdditionalParameters',

      initial: 'getInitial',
      pageIntro: 'getPageIntro',

      mobileBreakpoint: 'getMobileBreakpoint',
      windowInnerWidth: 'getWindowInnerWidth',

      calendarResultsItems: 'getCalendarResultsItems',
      resultsItems: 'getResultsItems'
    }),
    // availableFilters() {
    //   if (this.isAnEventCalendar) {
    //     const filters = [
    //       'keyword',
    //       'show_past_events',
    //     ];
    //
    //     if (!this.isAffiliateEvents) {
    //       filters.push('state')
    //       filters.push('event_type')
    //     }
    //
    //     return filters
    //   }
    //
    //   if (this.isBusinessItems) {
    //     return [
    //       'keyword',
    //       'business_item_type',
    //       'outcome',
    //       'strategic_objective'
    //     ]
    //   }
    // },
    featuredNodesClass() {
      return 'featured-events'
    },
    featuredNodesTitle() {
      if (this.isAnEventCalendar) {
        return 'Featured Events'
      }
    },
    isSearch() {
      return this.route === 'search'
    },
    isBusinessItems() {
      return this.route === 'business_items'
    },
    route () {
      return this.$route.name
    },
    title() {
      if(!this.search_settings[this.route].title) {
        return this.pageIntro[this.route] ? this.pageIntro[this.route].title : ''
      }
      return this.search_settings[this.route] && this.search_settings[this.route].title ? this.search_settings[this.route].title : ''
    },
    summary() {
      if(!this.search_settings[this.route].summary) {
        return this.pageIntro[this.route] ? this.pageIntro[this.route].summary : ''
      }
      return this.search_settings[this.route] && this.search_settings[this.route].summary ? this.search_settings[this.route].summary : ''
    },
    hasKeyword() {
      return true;
    },
    hasStateFilter() {
      return this.search_settings[this.route].state
    },
    hasEventTypeFilter() {
      return this.search_settings[this.route].event_type
    },
    hasIssueFilter() {
      return this.search_settings[this.route].issue
    },
    hasRoleFilter() {
      return this.search_settings[this.route].role
    },
    hasProfessionalPracticeFilter() {
      return this.search_settings[this.route].professional_practice
    },
    hasCampaignFilter() {
      return this.search_settings[this.route].campaign
    },
    hasShowPastEventsFilter() {
      return this.search_settings[this.route].show_past_events
    },
    hasBusinessItemTypeFilter() {
      return this.search_settings[this.route].business_item_type
    },
    hasOutcomeFilter() {
      return this.search_settings[this.route].outcome
    },
    hasStrategicObjectiveFilter() {
      return this.search_settings[this.route].strategic_objective
    },
  },
  methods: {
    getLastUpdatedDate() {
      let url = '';
      if (searchSettings.IS_MAIN_SITE) {
        url = this.isBusinessItems
          ? '/api/nbi/last-updated'
          : '/api/last_updated_date'
      }
      else {
        url = '/api/nea-search-last-updated-date-affiliate-events'
      }

      this.axios.get(url).then((res) => {
        this.last_updated = res.data
        // console.log(res)
      }).catch((err) => {
        console.log(err.response.data.message)
      }).finally(() => {

      })
    },
    datesSetChanged(datesSet){
      if(this.datesSet.startStr !== datesSet.startStr || this.datesSet.endStr !== datesSet.endStr) {
        this.$store.commit('updateDatesSet',  datesSet)
        if(!this.locked) {
          this.query()
        }
      }
    },
    query() {
      this.getLastUpdatedDate()

      let $this = this

      let data = this.packageData()

      if (this.isAnEventCalendar) {
        data.sort_field = {"page": "event_sort_date_and_time"}
        data.sort_direction = {"page": "asc"}
      }

      // remove parent issue filter if children are selected
      if (data.filters.page.issue) {
        // search issue filters
        data.filters.page.issue = this.removeParentIssue(data.filters.page.issue)
      }

      if(this.isBusinessItems) {
        data.sort_field = {"page": "sort"}
        data.sort_direction = {"page": "asc"}
      }

      data = this.removeUnusedFilters(data)

      this.packageUrl(data)

      data = this.setDefaultFilterValues(data)

      this.$store.commit('updateLocked', true)

      // console.log('calendarView ', this.calendarView)
      this.axios.post(this.api, data, this.headers)
          .then((response) => {

            $this.$store.commit('updateLocked', false)

            let dataPackage = {}
            dataPackage.currentCount = response.data.record_count
            dataPackage.totalCount = response.data.info.page.total_result_count
            dataPackage.totalPages = response.data.info.page.num_pages
            dataPackage.currentPage = response.data.info.page.current_page

            $this.$store.commit('updateResultsData', dataPackage)
            // if there is more than 1 page for this calendar view
            // query until all calendar results are fetched
            if($this.calendarView && response.data.info.page.num_pages > 1) {
              if(response.data.info.page.current_page === 1) {
                this.$store.commit('updateLocked', true)
                $this.$store.commit('updateResultsItems', []) // @TODO needed?
                $this.$store.commit('updateCalendarResultsItems', [...response.data.records.page])
                this.recursiveQuery(response, data, 2)
              }
            } else {
              $this.$store.commit('updateResultsItems', response.data.records.page)
              $this.$store.commit('updateFullResponse', response)
              $this.$store.commit('updateFacets', response.data.info.page.facets)

              if ($this.initial) {
                if (data.filters && data.filters.page) {
                  data.filters.page.state ? $this.updateInitialFilters($this.allStateFilters, data.filters.page.state) : ''
                  data.filters.page.event_type ? $this.updateInitialFilters($this.allEventTypeFilters, data.filters.page.event_type) : ''
                  data.filters.page.issue ? $this.updateInitialFilters($this.allIssueFilters, data.filters.page.issue) : ''
                  data.filters.page.campaign ? $this.updateInitialFilters($this.allCampaignFilters, data.filters.page.campaign) : ''
                  data.filters.page.role ? $this.updateInitialFilters($this.allRoleFilters, data.filters.page.role) : ''
                  data.filters.page.professional_practice ? $this.updateInitialFilters($this.allProfessionalPracticeFilters, data.filters.page.professional_practice) : ''
                  data.filters.page.business_item_type ? $this.updateInitialFilters($this.allBusinessItemTypeFilters, data.filters.page.business_item_type) : ''
                  data.filters.page.outcome ? $this.updateInitialFilters($this.allOutcomeFilters, data.filters.page.outcome) : ''
                  data.filters.page.region ? $this.updateInitialFilters($this.allRegionFilters, data.filters.page.region) : ''
                  data.filters.page.strategic_objective ? $this.updateInitialFilters($this.allStrategicObjectiveFilters, data.filters.page.strategic_objective) : ''
                  $this.hasShowPastEventsFilter && (data.filters.page.event_conclusion_date_and_time === undefined) ? $this.updateInitialFilters($this.allShowPastEventsFilters, ['1']) : ''
                }
                $this.$store.commit('updateInitial', false)
              }
              $this.newFacetCounts(data, response)
            }
          }).catch(function (error) {
      })
    },
    recursiveQuery(response, data, current_page) {
      data.page = current_page;
      let $this = this
      this.axios.post(this.api, data, this.headers)
        .then((res) => {
          if(res.data.info.page.current_page === response.data.info.page.num_pages) {
            this.$store.commit('updateResultsItems', [...$this.calendarResultsItems, ...res.data.records.page])
            this.$store.commit('updateCalendarResultsItems', [])
            $this.$store.commit('updateFullResponse', response)
            $this.$store.commit('updateFacets', response.data.info.page.facets)

            if ($this.initial) {
              if (data.filters && data.filters.page) {
                data.filters.page.state ? $this.updateInitialFilters($this.allStateFilters, data.filters.page.state) : ''
                data.filters.page.event_type ? $this.updateInitialFilters($this.allEventTypeFilters, data.filters.page.event_type) : ''
                data.filters.page.issue ? $this.updateInitialFilters($this.allIssueFilters, data.filters.page.issue) : ''
                data.filters.page.role ? $this.updateInitialFilters($this.allRoleFilters, data.filters.page.role) : ''
                data.filters.page.professional_practice ? $this.updateInitialFilters($this.allProfessionalPracticeFilters, data.filters.page.professional_practice) : ''
                data.filters.page.campaign ? $this.updateInitialFilters($this.allCampaignFilters, data.filters.page.campaign) : ''
                data.filters.page.region ? $this.updateInitialFilters($this.allRegionFilters, data.filters.page.region) : ''
                data.filters.page.business_item_type ? $this.updateInitialFilters($this.allBusinessItemTypeFilters, data.filters.page.business_item_type) : ''
                data.filters.page.outcome ? $this.updateInitialFilters($this.allOutcomeFilters, data.filters.page.outcome) : ''
                data.filters.page.strategic_objective ? $this.updateInitialFilters($this.allStrategicObjectiveFilters, data.filters.page.strategic_objective) : ''
                $this.hasShowPastEventsFilter && (data.filters.page.event_conclusion_date_and_time === undefined) ? $this.updateInitialFilters($this.allShowPastEventsFilters, ['1']) : ''
              }
              $this.$store.commit('updateInitial', false)
            }
            $this.newFacetCounts(data, response)
            this.$store.commit('updateLocked', false)
          }
          else {
            current_page = res.data.info.page.current_page + 1;
            this.$store.commit('updateCalendarResultsItems', [...$this.calendarResultsItems, ...res.data.records.page])
            this.recursiveQuery(response, data, current_page)
          }
        }).catch(function (error) {
      })
    },
    autocompleteQuery() {

      let $this = this

      let data = this.packageData()

      data = this.removeUnusedFilters(data)

      data = this.setDefaultFilterValues(data)

      data.q = this.autocompleteTerm;
      data.per_page = "5";
      data.page = "1";

      // remove parent issue filter if children are selected
      if (data.filters.page.issue) {
        // search issue filters
        data.filters.page.issue = this.removeParentIssue(data.filters.page.issue)
      }

      this.axios.post(this.api, data, this.headers)
          .then(function (response) {

            let dataPackage = {}
            dataPackage.currentCount = response.data.record_count
            dataPackage.totalCount = response.data.info.page.total_result_count
            dataPackage.totalPages = response.data.info.page.num_pages
            dataPackage.currentPage = response.data.info.page.current_page

            $this.$store.commit('updateAutocompleteResultsData', dataPackage)
            $this.$store.commit('updateAutocompleteResultsItems', response.data.records.page)

          }).catch(function (error) {
      })
    },
    newFacetCounts: function (queryData, responseData) {

      // @TODO refactor
      // @TODO simply do not update the facets for the taxonomy whose item you just clicked (for OR taxonomies)
      // @TODO only update it when another taxonomy is clicked

      // NOTE: Swiftype does not return counts for non-selected facet values once one of them is applied as a filter.
      // We need a separate query for each filter, removing the selected filter from query, to get the
      // counts for the non-selected facet values

      if (queryData && queryData.filters && queryData.filters.page) {
        this.$store.commit('updateNewStateCount', responseData.data.info.page.facets.state)
        this.$store.commit('updateNewEventTypeCount', responseData.data.info.page.facets.event_type)
        this.$store.commit('updateNewIssueCount', responseData.data.info.page.facets.issue)
        this.$store.commit('updateNewCampaignCount', responseData.data.info.page.facets.campaign)
        this.$store.commit('updateNewRoleCount', responseData.data.info.page.facets.role)
        this.$store.commit('updateNewProfessionalPracticeCount', responseData.data.info.page.facets.professional_practice)
        this.$store.commit('updateNewRegionCount', responseData.data.info.page.facets.region)

        const facets = [
          { field: 'state',
            commitFunction: 'updateNewStateCount'
          },
          { field: 'event_type',
            commitFunction: 'updateNewEventTypeCount'
          },
          { field: 'region',
            commitFunction: 'updateNewRegionCount'
          },
          { field: 'business_item_type',
            commitFunction: 'updateNewBusinessItemTypeCount'
          },
          { field: 'outcome',
            commitFunction: 'updateNewOutcomeCount'
          },
          { field: 'strategic_objective',
            commitFunction: 'updateNewStrategicObjectiveCount'
          },
          { field: 'issue',
            commitFunction: 'updateNewIssueCount'
          },
          { field: 'professional_practice',
            commitFunction: 'updateNewProfessionalPracticeCount'
          },
          { field: 'role',
            commitFunction: 'updateNewRoleCount'
          },
          { field: 'campaign',
            commitFunction: 'updateNewCampaignCount'
          },
        ];

        // Get an updated count for each facet.
        facets.forEach((facet) => {
          if (queryData.filters.page[facet.field]?.length > 0) {
            let data = JSON.parse(JSON.stringify(queryData))
            delete data.filters.page[facet.field]

            // Always send through the Eye on Equity filter for OEA Event Calendar
            // so that the facet counts are 0 for other event types. Question: is
            // this the best place to enforce this?
            if (facet.field === 'event_type' && this.isOEA && this.isAnEventCalendar) {
              // Removing as per NEA241-92, but commenting out instead of
              // deleting just in case this ever needs to be reverted.
              // data.filters.page.event_type = [eyeOnEquityTid]
            }

            // Commit the new facet count to the store.
            this.axios.post(this.api, data, this.headers)
              .then((response) => {
                if (response?.data?.info?.page?.facets) {
                  this.$store.commit(facet.commitFunction, response.data.info.page.facets[facet.field])
                }
              })
          }
        })
      }
    },
    getActiveFiltersByKind(kind) {
      let set = this.activeFilters.filter(el => {
        return el.kind === kind
      })

      let values = set.map(el => {
        return el.value
      })

      return values.length > 0 ? values : []
    },
    packageUrl(data) {
      let fullUrlString = this.$route.path + '?'

      if (this.initial) {
        const searchString = String(window.location.search)
        const args = new URLSearchParams(searchString)
        this.additionalParameters.forEach((parameter) => {
          if (args.has(parameter)) {
            this.additional_parameters_string += args.get(parameter) ? (parameter + '=' + args.get(parameter) + '&') : '';
          }
        });
      }

      if (data.q && data.q !== '') {
        fullUrlString += "q="
        fullUrlString += data.q
        fullUrlString += "&"
      }
      //show_past_events / event_conclusion_date_and_time
      if(this.getActiveFiltersByKind('show_past_events').length !== 0) {
        fullUrlString += 'show_past_events=1&'
      }

      // @TODO refactor
      if (data.filters && data.filters.page && data.filters.page && Object.keys(data.filters.page).length > 0) {
        Object.keys(data.filters.page).forEach(key => {
          // AND within filters
          if (data.filters.page[key] && data.filters.page[key].values && data.filters.page[key].values.length > 0) {
            let parameters = {}
            Object.keys(data.filters.page[key]).forEach(index => {
              let parameter = this.getParameter(key, data.filters.page[key][index])
              let value = data.filters.page[key][index]
              if (parameters[parameter] === undefined) {
                parameters[parameter] = []
              }
              parameters[parameter].push(value)
            })
            Object.keys(parameters).forEach(parameter => {
              fullUrlString += parameter
              fullUrlString += "="
              fullUrlString += parameters[parameter].toString()
              fullUrlString += "&"
            })
          }
          // OR within filters
          else if (data.filters.page[key] && data.filters.page[key].length > 0) {
            let parameters = {}
            Object.keys(data.filters.page[key]).forEach(index => {
              let parameter = this.getParameter(key, data.filters.page[key][index])
              let value = data.filters.page[key][index]
              if (parameters[parameter] === undefined) {
                parameters[parameter] = []
              }
              parameters[parameter].push(value)
            })
            Object.keys(parameters).forEach(parameter => {
              fullUrlString += parameter
              fullUrlString += "="
              fullUrlString += parameters[parameter].toString()
              fullUrlString += "&"
            })
          }
        })
      }

      fullUrlString += this.additional_parameters_string
      history.replaceState({}, this.$route.path, fullUrlString)
    },
    removeUnusedFilters(data) {
      const filters = [
        'business_item_type',
        'event_conclusion_date_and_time',
        'event_start_date_and_time',
        'event_type',
        'outcome',
        'region',
        'state',
        'strategic_objective',
        'type',
        'issue',
        'role',
        'campaign',
        'professional_practice'
      ];

      filters.forEach((filter) => {
        if (data.filters?.page[filter]) {
          if (data.filters.page[filter]?.length === 0 || JSON.stringify(data.filters.page[filter]) === '{}') {
            delete data.filters.page[filter]
          }
        }
      })

      return data
    },
    setDefaultFilterValues(data) {

      if (this.isAnEventCalendar) {
        data.filters.page.type = ["resource"]
        data.filters.page.resource_type = [eventTid]
        data.filters.page.has_dates = "1"
      }

      if(this.isBusinessItems) {
        data.filters.page.type = ["business_item"]
      }

      if(this.isAffiliateEvents) {
        data.filters.page.is_ra_event_resource = ["!1"]
      }

      return data
    },
    updateInitialFilters(filters, values) {
      // @TODO
      if (filters && values) {
        values.forEach(value => {
          for (const [i, filter] of Object.entries(filters)) {
            if (filter.value === value) {
              let activeFilters = this.activeFilters
              activeFilters.push(filter)
              this.$store.commit('updateActiveFilters', activeFilters)
            }
            if (filter.children) {
              for (const [i, child] of Object.entries(filter.children)) {
                if (child.value === value) {
                  let activeFilters = this.activeFilters
                  activeFilters.push(child)
                  activeFilters.push(filter)
                  this.$store.commit('updateActiveFilters', activeFilters)
                }
              }
            }
          }
        })
      }
    },
    removeParentIssue(data) {
      // look at each issue filter
      for (const [i, filter] of Object.entries(this.allIssueFilters)) {
        // if the filter is a parent and selected
        if (filter.children && data.includes(filter.value)) {
          // look at each filter child
          for (const [j, child] of Object.entries(filter.children)) {
            // if a child is also selected
            if (filter.children && data.includes(child.value)) {
              // then remove the parent from query
              let index = data.indexOf(filter.value)
              if (index !== -1) {
                data.splice(index, 1);
              }
            }
          }
        }
      }
      return data
    },
    packageData() {
      const searchString = String(window.location.search)
      const args = new URLSearchParams(searchString)
      //@TODO in calendar view, all events for the selected month are accessible
      let data
      let dateRange = {}
      if (this.initial) {
        // this is the initial query, sent as the page loads.
        // Prepare the 'data' variable, which holds all the information we need for our Axios call to the Swiftype API.
        // This branch is for the 'initial' query, which happens only when the page loads. It gets paramters from the URL.

        let q = (args.has('q') ? args.get('q') : '')

        if (q !== this.searchTerm) {
          this.$store.commit('updateSearchTerm', q)
          this.$store.commit('updateAutocompleteTerm', q)
        }

        if (!args.has('show_past_events')) {
            dateRange.type = "range"
            dateRange.from = this.today
        }

        data = {
          "q": ( this.hasKeyword && args.has('q') ? args.get('q') : ''),
          "per_page": this.perPage,
          "facets": {
            "page": ["type", "state", "event_type", "event_conclusion_date_and_time", "business_item_type", "outcome", "region", "strategic_objective", "issue", "campaign", "role", "professional_practice"]
          },
          "page": this.currentPage,
          "filters": {
            "page": {
              "type": [],
              "state": this.hasStateFilter && args.has('state') ? args.get('state').split(',') : [],
              "event_type": this.hasEventTypeFilter && args.has('event_type') ? args.get('event_type').split(',') : [],
              "issue": this.hasIssueFilter && args.has('issue') ? args.get('issue').split(',') : [],
              "role": this.hasRoleFilter && args.has('role') ? args.get('role').split(',') : [],
              "professional_practice": this.hasProfessionalPracticeFilter && args.has('professional_practice') ? args.get('professional_practice').split(',') : [],
              "campaign": this.hasCampaignFilter && args.has('campaign') ? args.get('campaign').split(',') : [],
              "event_conclusion_date_and_time": this.hasShowPastEventsFilter ? dateRange : {},
              "business_item_type": this.hasBusinessItemTypeFilter && args.has('business_item_type') ? args.get('business_item_type').split(',') : [],
              "outcome": this.hasOutcomeFilter && args.has('outcome') ? args.get('outcome').split(',') : [],
              "region": this.hasRegionFilter && args.has('region') ? args.get('region').split(',') : [],
              "strategic_objective": this.hasStrategicObjectiveFilter && args.has('strategic_objective') ? args.get('strategic_objective').split(',') : []
            }
          },
        }
        // end intiial query
      } else {
        // This is for the standard query (used anytime we're not 'initial'). It uses the Vuex store to determine which options to send.
        // factor out into this.buildStandardQuery()
        if (this.getActiveFiltersByKind('show_past_events').length === 0) {
            dateRange.type = "range"
            dateRange.from = this.today
        }

        data = {
          "q": this.hasKeyword ? this.searchTerm : '',
          "per_page": this.perPage,
          "facets": {
            "page": ["type", "state", "event_type", "campaign", "role", "issue", "professional_practice", "event_conclusion_date_and_time", "business_item_type", "outcome", "region", "strategic_objective"]
          },
          "page": this.currentPage,
          "filters": {
            "page": {
              "type": [],
              "state": this.hasStateFilter ? this.getActiveFiltersByKind('state') : [],
              "event_type": this.hasEventTypeFilter ? this.getActiveFiltersByKind('event_type') : [],
              "issue": this.hasIssueFilter ? this.getActiveFiltersByKind('issue') : [],
              "role": this.hasRoleFilter ? this.getActiveFiltersByKind('role') : [],
              "professional_practice": this.hasProfessionalPracticeFilter ? this.getActiveFiltersByKind('professional_practice') : [],
              "campaign": this.hasCampaignFilter ? this.getActiveFiltersByKind('campaign') : [],
              "event_conclusion_date_and_time": this.hasShowPastEventsFilter ? dateRange : {},
              "business_item_type": this.hasBusinessItemTypeFilter ? this.getActiveFiltersByKind('business_item_type') : [],
              "outcome": this.hasOutcomeFilter ? this.getActiveFiltersByKind('outcome') : [],
              "region": this.hasRegionFilter ? this.getActiveFiltersByKind('region') : [],
              "strategic_objective": this.hasStrategicObjectiveFilter ? this.getActiveFiltersByKind('strategic_objective') : []
            }
          },
        }
      }
      return data
    },
    getParameter(filter_kind, filter_value) {
      let filters = {}
      if (filter_kind === "group_one") {
        filters = this.allGroupOneFilters
      } else {
        return filter_kind
      }
      for (const [index, filter] of Object.entries(filters)) {
        if (filter.kind === filter_kind && filter.value === filter_value) {
          return filter.parameter
        }
      }
    },
  },
  data() {
    return {
      api: `https://search-api.swiftype.com/api/v1/public/engines/search.json?engine_key=${engineKey}`,
      featured_nodes: null,
      search_settings: searchSettings,
      headers: {
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
          'Access-Control-Allow-Origin': '*',
          'Accept': 'application/json'
        }
      },
      last_updated: '',
      additional_parameters_string: '',
    }
  },
  mounted() {
    if ((this.isCalendarOfEvents || this.isAffiliateEvents) && this.windowInnerWidth > this.mobileBreakpoint) {
      this.$store.commit('updateCalendarView', true)
    }
    if (this.isAnEventCalendar && this.calendarView) {
      this.$store.commit('updatePerPage', this.perPageCalendar)
    }
    this.$store.commit('updateToday',  new Date().toISOString().slice(0, 10))
    this.$store.commit('updateAllTypeFilters', typeFilters)
    this.$store.commit('updateAllResourceTypeFilters', resourceTypeFilters)
    this.$store.commit('updateAllStateFilters', stateFilters)
    this.$store.commit('updateAllEventTypeFilters', eventTypeFilters)
    this.$store.commit('updateAllRegionFilters', regionFilters)
    this.$store.commit('updateAllShowPastEventsFilters', showPastEventsFilters)
    this.$store.commit('updateAllBusinessItemTypeFilters', businessItemTypeFilters)
    this.$store.commit('updateAllOutcomeFilters', outcomeFilters)
    this.$store.commit('updateAllStrategicObjectiveFilters', strategicObjectiveFilters)
    this.$store.commit('updateAllIssueFilters', issueFilters)
    this.$store.commit('updateAllCampaignFilters', campaignFilters)
    this.$store.commit('updateAllProfessionalPracticeFilters', professionalPracticeFilters)
    this.$store.commit('updateAllRoleFilters', roleFilters)
    this.query()
    this.featured_nodes = featuredNodes
  },
}

</script>
